import { CASCADE_TRACING_API_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  CreateOrderHubOrderPayload,
  OrderHubListResponse,
  OrderHubOrder,
  OrderHubOrderLine,
  UpdateOrderHubOrderPayload,
} from './types';

export async function fetchOrderHubOrders(
  qs: string,
): Promise<OrderHubListResponse<OrderHubOrder>> {
  return apiFetch<OrderHubListResponse<OrderHubOrder>>(
    `/orders?${qs}`,
    {},
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchOrderHubOrder(
  id: string,
): Promise<{ data: OrderHubOrder }> {
  return apiFetch<{ data: OrderHubOrder }>(
    `/orders/${id}`,
    {},
    CASCADE_TRACING_API_URL,
  );
}

export async function createOrderHubOrder(
  payload: CreateOrderHubOrderPayload,
): Promise<{ data: OrderHubOrder }> {
  return apiFetch<{ data: OrderHubOrder }>(
    '/orders',
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function updateOrderHubOrder(
  id: string,
  payload: UpdateOrderHubOrderPayload,
): Promise<{ data: OrderHubOrder }> {
  return apiFetch<{ data: OrderHubOrder }>(
    `/orders/${id}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function deleteOrderHubOrder(id: string): Promise<void> {
  return apiFetch<void>(
    `/orders/${id}`,
    { method: 'DELETE' },
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchOrderHubOrderLines(
  qs: string,
): Promise<OrderHubListResponse<OrderHubOrderLine>> {
  return apiFetch<OrderHubListResponse<OrderHubOrderLine>>(
    `/order-lines?${qs}`,
    {},
    CASCADE_TRACING_API_URL,
  );
}

/**
 * Fetches EVERY line of an order by paging through the 100-capped list endpoint.
 * The declarative full-sync `PATCH /orders/:id` treats any omitted line as a
 * delete, so a line write must be built from the complete current set — never
 * the display query, which is capped at one page.
 */
export async function fetchAllOrderHubOrderLines(
  orderId: string,
): Promise<OrderHubOrderLine[]> {
  const PAGE_SIZE = 100;
  const all: OrderHubOrderLine[] = [];
  let page = 0;
  while (true) {
    const qs = new URLSearchParams({
      orderId,
      page: String(page),
      count: String(PAGE_SIZE),
    }).toString();
    const res = await fetchOrderHubOrderLines(qs);
    all.push(...res.data);
    if (res.data.length === 0 || all.length >= res.meta.totalCount) break;
    page += 1;
  }
  return all;
}
